import { useCallback, useEffect, useState } from "react";
import { KeyRound, Plus, Settings as SettingsIcon, ShieldCheck, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, CATEGORIES, errMsg } from "../lib/api";
import { PageHeader, UserAvatar } from "../components/ui-bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function Settings() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [newCategory, setNewCategory] = useState("");

  const load = useCallback(async () => {
    try {
      const [u, s] = await Promise.all([api.get("/users"), api.get("/settings")]);
      setUsers(u.data);
      setSettings(s.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, []);

  useEffect(() => {
    if (user.role === "owner") load();
  }, [load, user.role]);

  if (user.role !== "owner") {
    return (
      <div className="rounded-2xl border border-[#1D225C]/10 bg-white p-10 text-center" data-testid="settings-forbidden">
        <ShieldCheck className="mx-auto mb-3 h-8 w-8 text-[#B88A4A]" />
        <p className="font-display text-lg font-semibold text-[#1D225C]">Nur für Manuela</p>
        <p className="mt-1 text-sm text-[#777]">Die Verwaltungseinstellungen sind nur für die Agentur-Inhaberin zugänglich.</p>
      </div>
    );
  }

  const saveSettings = async (updates) => {
    try {
      const { data } = await api.put("/settings", updates);
      setSettings(data);
      toast.success("Einstellungen gespeichert");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const setPassword = async () => {
    if (newPw.length < 8) return toast.error("Mindestens 8 Zeichen");
    try {
      await api.post(`/users/${pwFor.id}/set-password`, { new_password: newPw });
      toast.success(`Passwort für ${pwFor.name} geändert`);
      setPwFor(null);
      setNewPw("");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const addCategory = () => {
    const key = newCategory.trim().toLowerCase().replace(/\s+/g, "_");
    if (!key) return;
    const cats = settings.project_categories || [];
    if (cats.includes(key)) return toast.error("Kategorie existiert bereits");
    saveSettings({ project_categories: [...cats, key] });
    setNewCategory("");
  };

  const removeCategory = (key) => {
    saveSettings({ project_categories: (settings.project_categories || []).filter((c) => c !== key) });
  };

  return (
    <div className="animate-fade-up space-y-6" data-testid="settings-page">
      <PageHeader overline="Verwaltung" title="Einstellungen" testid="settings-header" />

      <section className="rounded-2xl border border-[#1D225C]/10 bg-white p-6 shadow-sm" data-testid="settings-users">
        <h3 className="mb-4 flex items-center gap-2 font-display text-base font-semibold text-[#1D225C]">
          <Users className="h-4 w-4 text-[#B88A4A]" /> Benutzer
        </h3>
        <div className="space-y-3">
          {users.map((u) => (
            <div key={u.id} data-testid={`settings-user-${u.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-[#1D225C]/5 bg-[#F7F6F2] px-4 py-3">
              <UserAvatar name={u.name} color={u.color} avatar={u.avatar} size={9} />
              <div className="w-full min-w-0 sm:flex-1">
                <p className="text-sm font-semibold text-[#1D225C]">{u.name}</p>
                <p className="break-all text-xs text-[#777]">{u.email} · {u.role === "owner" ? "Agentur-Inhaberin" : "Webdesigner / Entwickler"}</p>
              </div>
              <Button variant="outline" size="sm" data-testid={`settings-reset-pw-${u.id}`} onClick={() => { setPwFor(u); setNewPw(""); }} className="w-full rounded-lg text-xs sm:w-auto">
                <KeyRound className="mr-1.5 h-3.5 w-3.5" /> Passwort zurücksetzen
              </Button>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-[#1D225C]/10 bg-white p-6 shadow-sm" data-testid="settings-permissions">
        <h3 className="mb-4 flex items-center gap-2 font-display text-base font-semibold text-[#1D225C]">
          <ShieldCheck className="h-4 w-4 text-[#B88A4A]" /> Berechtigungen
        </h3>
        <label className="flex items-center justify-between gap-4">
          <span>
            <span className="block text-sm font-medium text-[#1D225C]">Mondher darf Projekte anlegen</span>
            <span className="block text-xs text-[#777]">Wenn deaktiviert, können nur du neue Projekte erstellen.</span>
          </span>
          <Switch
            data-testid="settings-allow-designer-projects"
            checked={settings?.allow_designer_projects ?? true}
            onCheckedChange={(v) => saveSettings({ allow_designer_projects: v })}
          />
        </label>
      </section>

      <section className="rounded-2xl border border-[#1D225C]/10 bg-white p-6 shadow-sm" data-testid="settings-categories">
        <h3 className="mb-4 flex items-center gap-2 font-display text-base font-semibold text-[#1D225C]">
          <SettingsIcon className="h-4 w-4 text-[#B88A4A]" /> Projekt-Kategorien
        </h3>
        <div className="mb-3 flex flex-wrap gap-2">
          {(settings?.project_categories || []).map((c) => (
            <span key={c} className="flex items-center gap-1.5 rounded-full border border-[#1D225C]/10 bg-[#F7F6F2] px-3 py-1.5 text-xs font-medium text-[#1D225C]" data-testid={`settings-category-${c}`}>
              {CATEGORIES[c] || c}
              <button data-testid={`settings-category-remove-${c}`} onClick={() => removeCategory(c)} className="text-[#999] hover:text-red-500">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="settings-new-category"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCategory()}
            placeholder="Neue Kategorie…"
            className="max-w-xs rounded-xl"
          />
          <Button data-testid="settings-add-category" onClick={addCategory} variant="outline" className="rounded-xl">
            <Plus className="mr-1 h-4 w-4" /> Hinzufügen
          </Button>
        </div>
      </section>

      <section className="rounded-2xl border border-[#1D225C]/10 bg-white p-6 shadow-sm" data-testid="settings-security">
        <h3 className="mb-3 flex items-center gap-2 font-display text-base font-semibold text-[#1D225C]">
          <ShieldCheck className="h-4 w-4 text-[#B88A4A]" /> Sicherheit
        </h3>
        <ul className="space-y-1.5 text-sm text-[#555]">
          <li>· Passwörter werden mit bcrypt gehasht gespeichert.</li>
          <li>· Zugangsdaten im Tresor sind verschlüsselt (Fernet/AES) und nur nach Passwort-Bestätigung sichtbar.</li>
          <li>· Sitzungen laufen über sichere httpOnly-Cookies; nach 5 Fehlversuchen wird das Konto 15 Minuten gesperrt.</li>
          <li>· Alle API-Endpunkte erfordern eine aktive Sitzung.</li>
        </ul>
      </section>

      <Dialog open={!!pwFor} onOpenChange={(v) => !v && setPwFor(null)}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="settings-pw-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg text-[#1D225C]">Passwort zurücksetzen</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#777]">Neues Passwort für <strong>{pwFor?.name}</strong>:</p>
          <div>
            <Label>Neues Passwort (min. 8 Zeichen)</Label>
            <Input data-testid="settings-pw-input" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="mt-1.5" autoFocus />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="settings-pw-cancel" onClick={() => setPwFor(null)} className="rounded-xl">Abbrechen</Button>
            <Button data-testid="settings-pw-submit" onClick={setPassword} className="rounded-xl bg-[#1D225C] text-white hover:bg-[#2a3070]">Speichern</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
